/** @file DSGVO Art. 17 account deletion: wipe user docs + auth user. */
import { deleteDoc, getDocs, query, where, writeBatch } from "firebase/firestore";
import { deleteUser } from "firebase/auth";
import { getDb } from "./firebase.js";
import { playsCol, sharesCol, teamsCol, userDoc } from "./db.js";

/** @typedef {import("firebase/auth").User} User */

/**
 * @param {import("firebase/firestore").CollectionReference<any>} colRef
 */
async function deleteAllDocs(colRef) {
  const snap = await getDocs(colRef);
  if (snap.empty) {
    return;
  }
  const db = getDb();
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

/**
 * Löscht alle `shares/{shareId}` Docs, deren `ownerId` dem gegebenen uid entspricht.
 * Batch-committet in Chunks ≤400, um unter dem 500-Op-Limit zu bleiben.
 * @param {string} uid
 * @returns {Promise<void>}
 */
async function deleteSharesForOwner(uid) {
  const q = query(sharesCol(), where("ownerId", "==", uid));
  const snap = await getDocs(q);
  if (snap.empty) {
    return;
  }
  const db = getDb();
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const CHUNK = 400;
  for (let i = 0; i < snap.docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + CHUNK).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

/**
 * Ordered: shares → plays → teams → user doc → auth user.
 * Reauth is delegated to the caller via `reauthHandler(providerId)`.
 * @param {User} user
 * @param {(providerId: string) => Promise<void>} reauthHandler
 */
export async function deleteAccountAndData(user, reauthHandler) {
  const uid = user.uid;
  await deleteSharesForOwner(uid);
  await deleteAllDocs(playsCol(uid));
  await deleteAllDocs(teamsCol(uid));
  await deleteDoc(userDoc(uid));

  try {
    await deleteUser(user);
  } catch (err) {
    if (err && typeof err === "object" && "code" in err && err.code === "auth/requires-recent-login") {
      const providerId = user.providerData[0]?.providerId ?? "password";
      await reauthHandler(providerId);
      await deleteUser(user);
    } else {
      throw err;
    }
  }
}
